import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, User, Music, Video, Image, MapPin, Calendar, ExternalLink, Users, Lock } from 'lucide-react';
import DeepWaveCrew from '../components/DeepWaveCrew';
import BandMessage from '../components/BandMessage';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TheWave = () => {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [siteLogo, setSiteLogo] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);

    // Site logo doesn't depend on auth — fetch it right away.
    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const siteResponse = await api.get('/admin/settings/public');
                if (siteResponse.data.success) {
                    const logoUrl = siteResponse.data.data.site_logo;
                    if (logoUrl) setSiteLogo(logoUrl);
                }
            } catch (settingsError) {
                console.error('Error fetching site settings:', settingsError);
            } finally {
                setPageLoading(false);
            }
        };
        fetchSiteSettings();
    }, []);

    // Community member data is only fetched once we know the visitor is
    // logged in — logged-out visitors never receive it over the wire, rather
    // than having it sent and merely hidden behind a blur/overlay.
    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            setUsers([]);
            setFilteredUsers([]);
            setUsersLoading(false);
            return;
        }
        let cancelled = false;
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const usersResponse = await fetch('/api/users/public');
                const usersResult = await usersResponse.json();
                if (!cancelled && usersResult.success) {
                    setUsers(usersResult.data);
                    setFilteredUsers(usersResult.data);
                }
            } catch (error) {
                console.error('Error fetching community members:', error);
            } finally {
                if (!cancelled) setUsersLoading(false);
            }
        };
        fetchUsers();
        return () => { cancelled = true; };
    }, [isAuthenticated, authLoading]);

    useEffect(() => {
        const filtered = users.filter(user =>
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.bio?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    if (pageLoading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center">
                        {siteLogo ? (
                            <div className="w-24 h-24 mx-auto mb-4 animate-float">
                                <img
                                    src={siteLogo}
                                    alt="The Deep Wave Logo"
                                    width="96"
                                    height="96"
                                    className="w-full h-full object-cover rounded-lg"
                                />
                            </div>
                        ) : (
                            <div className="text-6xl mb-4 animate-float">🌊</div>
                        )}
                        <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                        <p className="text-gray-400">Loading community members...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-6xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-12">
                    {siteLogo ? (
                        <div className="w-32 h-32 mx-auto mb-4">
                            <img
                                src={siteLogo}
                                alt="The Deep Wave Logo"
                                width="128"
                                height="128"
                                className="w-full h-full object-cover rounded-lg shadow-lg"
                            />
                        </div>
                    ) : (
                        <div className="text-6xl mb-4">🌊</div>
                    )}
                    <h1 className="text-5xl font-black gradient-text mb-4">Ensemble Musicians</h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Here are the talented musicians and creators in The Deep Wave ensemble. 
                        Connect with artists, join the community and explore artistic expressions.
                    </p>
                </div>

                {/* Crew Section */}
                <div className="mb-12">
                    <DeepWaveCrew limit={null} showTitle={true} showViewAll={false} expanded={true} />
                </div>

                <BandMessage />

                {/* Community Section — entirely hidden for logged-out visitors, not just blurred */}
                <div className="relative">
                    {authLoading || (isAuthenticated && usersLoading) ? (
                        <div className="text-center py-16">
                            <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                            <p className="text-gray-400">Loading community members...</p>
                        </div>
                    ) : !isAuthenticated ? (
                        <div className="flex justify-center">
                            <div className="bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 rounded-2xl border border-purple-500/30 p-8 max-w-md w-full mx-4 shadow-2xl text-center">
                                <div className="flex justify-center items-center mb-6 relative">
                                    <div className="absolute animate-pulse">
                                        <Users className="w-16 h-16 text-purple-400 opacity-20" />
                                    </div>
                                    <Lock className="w-8 h-8 text-purple-400 animate-bounce" />
                                </div>

                                <h2 className="text-2xl font-bold text-white mb-3">
                                    Login to View the Full Community
                                </h2>

                                <p className="text-gray-300 mb-6 leading-relaxed">
                                    Join The Deep Wave to connect with fellow fans, artists, and creators.
                                    <span className="text-purple-400 font-semibold"> It's free and takes just a minute!</span>
                                </p>

                                <div className="bg-gray-800/50 rounded-xl p-4 mb-6 text-left">
                                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                                        <Users className="w-4 h-4 text-blue-400" />
                                        Join to unlock:
                                    </h3>
                                    <ul className="space-y-2 text-sm text-gray-300">
                                        <li className="flex items-center gap-2">
                                            <span className="text-purple-400">🌊</span>
                                            Browse and connect with community members
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-blue-400">💬</span>
                                            Comment and engage with creators
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-400">🎵</span>
                                            Upload and share your own content
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-pink-400">❤️</span>
                                            Like and support your favorite artists
                                        </li>
                                    </ul>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Link
                                        to="/register"
                                        className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg"
                                    >
                                        Create Account
                                    </Link>
                                    <Link
                                        to="/login"
                                        className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors border border-gray-600 hover:border-gray-500"
                                    >
                                        Login
                                    </Link>
                                </div>

                                <p className="text-xs text-gray-500 mt-4">
                                    Already have an account? Just click Login above!
                                </p>
                            </div>
                        </div>
                    ) : (
                    <>
                        {/* Community Section Header */}
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold gradient-text mb-4 flex items-center justify-center gap-2">
                                <Users className="w-8 h-8 text-purple-400" />
                                <span>Community</span>
                            </h2>
                            <p className="text-gray-400 max-w-xl mx-auto">
                                Connect with fellow fans, artists, and creators in our vibrant community.
                            </p>
                        </div>

                        {/* Search Bar */}
                        <div className="mb-8">
                            <div className="relative max-w-md mx-auto">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search community members..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="text-center mb-8">
                            <p className="text-gray-400">
                                <span className="text-purple-400 font-semibold">{filteredUsers.length}</span> community members
                            </p>
                        </div>

                        {/* Users Grid */}
                        <div className="flex justify-center">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl w-full">
                            {filteredUsers.map((user) => (
                                <div key={user.id} className="glass-card p-3 sm:p-4 hover:transform hover:scale-105 transition-all duration-300">
                                    {/* Profile Picture */}
                                    <div className="flex flex-col items-center mb-4">
                                        <div className="relative">
                                            {user.profile_picture_url ? (
                                                <img
                                                    src={user.profile_picture_url}
                                                    alt={user.display_name || user.username}
                                                    width="120"
                                                    height="120"
                                                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto object-cover border-2 border-purple-500/50"
                                                />
                                            ) : (
                                                <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto flex items-center justify-center">
                                                    <span className="text-white font-bold text-3xl">
                                                        {(user.display_name || user.username)?.charAt(0)?.toUpperCase() || 'U'}
                                                    </span>
                                                </div>
                                            )}

                                            {Boolean(user.is_artist) && (
                                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center border-2 border-gray-900">
                                                    <Music className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* User Info */}
                                    <div className="text-center mb-4">
                                        <h3 className="text-lg font-bold text-white mb-1">
                                            {user.display_name || user.username}
                                        </h3>
                                        <p className="text-purple-400 text-sm mb-2">@{user.username}</p>

                                        {user.bio && (
                                            <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                                                {user.bio}
                                            </p>
                                        )}
                                    </div>

                                    {/* Stats */}
                                    <div className="flex justify-center gap-4 mb-4 text-xs text-gray-400">
                                        {Boolean(user.upload_counts) && (
                                            <>
                                                {Boolean(user.upload_counts.audio > 0) && (
                                                    <div className="flex items-center gap-1">
                                                        <Music className="w-3 h-3" />
                                                        <span>{user.upload_counts.audio}</span>
                                                    </div>
                                                )}
                                                {Boolean(user.upload_counts.video > 0) && (
                                                    <div className="flex items-center gap-1">
                                                        <Video className="w-3 h-3" />
                                                        <span>{user.upload_counts.video}</span>
                                                    </div>
                                                )}
                                                {Boolean(user.upload_counts.image > 0) && (
                                                    <div className="flex items-center gap-1">
                                                        <Image className="w-3 h-3" />
                                                        <span>{user.upload_counts.image}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Additional Info */}
                                    <div className="space-y-2 mb-4">
                                        {Boolean(user.location) && (
                                            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                                                <MapPin className="w-3 h-3" />
                                                <span>{user.location}</span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                                            <Calendar className="w-3 h-3" />
                                            <span>Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                                        </div>
                                    </div>

                                    {/* View Profile Button */}
                                    <a
                                        href={`/profile/${user.username}`}
                                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                                    >
                                        <span>View Profile</span>
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            ))}
                            </div>
                        </div>

                        {/* Empty State */}
                        {filteredUsers.length === 0 && !usersLoading && (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-xl font-bold text-gray-300 mb-2">No creators found</h3>
                                <p className="text-gray-400">
                                    {searchTerm ? 'Try adjusting your search terms.' : 'Be the first to join The Wave!'}
                                </p>
                            </div>
                        )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TheWave;